import axios from 'axios';
import { attachErrorNormalizer } from './api';

const API_URL = '/api';

const authHeaders = () => {
  const adminToken = localStorage.getItem('luxortek_admin_token');
  return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
};

const api = axios.create({ baseURL: API_URL, timeout: 45000 });
api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...authHeaders() };
  return config;
});
attachErrorNormalizer(api);

// ─── Templates + Blocks ───────────────────────────────────────────────
export const getCampaignTemplates = () => api.get('/admin/campaigns/templates');
export const renderTemplateBody = (key) => api.post(`/admin/campaigns/templates/${key}/render`);
export const renderBlock = (key, params = {}) => api.post('/admin/campaigns/blocks/render', { key, params });

// ─── Filters (countries / segments) ───────────────────────────────────
export const getSubscriberFilters = () => api.get('/admin/subscribers/filters');

// ─── Campaign CRUD ────────────────────────────────────────────────────
export const listCampaigns = () => api.get('/admin/campaigns');
export const createCampaign = (data) => api.post('/admin/campaigns', data);
export const getCampaign = (id) => api.get(`/admin/campaigns/${id}`);
export const updateCampaign = (id, data) => api.put(`/admin/campaigns/${id}`, data);
export const duplicateCampaign = (id) => api.post(`/admin/campaigns/${id}/duplicate`);

// ─── Campaign Templates (Phase 1 · persistent library) ────────────────
export const listCampaignTemplateLibrary = (category) =>
  api.get('/admin/campaign-templates', { params: category ? { category } : {} });
export const getCampaignTemplate = (id) => api.get(`/admin/campaign-templates/${id}`);
export const saveCampaignTemplate = (body) => api.post('/admin/campaign-templates', body);
export const deleteCampaignTemplate = (id) => api.delete(`/admin/campaign-templates/${id}`);
export const deleteCampaign = (id) => api.delete(`/admin/campaigns/${id}`);
export const getCampaignAudience = (id) => api.post(`/admin/campaigns/${id}/audience`);
export const getAudienceBreakdown = (payload) => api.post('/admin/campaigns/audience-breakdown', payload);
export const sendTestEmail = (id, to_email) => api.post(`/admin/campaigns/${id}/test`, { to_email });
export const sendCampaign = (id) => api.post(`/admin/campaigns/${id}/send`);
export const getCampaignRecipients = (id, { filter = 'all', search = '' } = {}) =>
  api.get(`/admin/campaigns/${id}/recipients`, { params: { filter, search } });

// ─── Email Analytics ──────────────────────────────────────────────────
export const getEmailAnalyticsSummary = () => api.get('/admin/email-analytics/summary');
export const getEmailAnalyticsTopCountries = () => api.get('/admin/email-analytics/top-countries');
export const getEmailAnalyticsCampaigns = () => api.get('/admin/email-analytics/campaigns');

// ─── Subscribers (extended) ───────────────────────────────────────────
export const patchSubscriber = (id, data) => api.patch(`/admin/subscribers/${id}`, data);

// ─── Public unsubscribe ───────────────────────────────────────────────
export const getUnsubscribeInfo = (token) => axios.get(`${API_URL}/email/unsubscribe/${token}`);
export const confirmUnsubscribe = (token, reason) => axios.post(`${API_URL}/email/unsubscribe/${token}`, { reason });

// ─── Sender Profiles ──────────────────────────────────────────────────
export const listSenderProfiles = () => api.get('/admin/sender-profiles');
export const createSenderProfile = (data) => api.post('/admin/sender-profiles', data);
export const updateSenderProfile = (id, data) => api.put(`/admin/sender-profiles/${id}`, data);
export const deleteSenderProfile = (id) => api.delete(`/admin/sender-profiles/${id}`);
export const setDefaultSenderProfile = (id) => api.post(`/admin/sender-profiles/${id}/set-default`);
export const testSenderProfile = (id, to_email) => api.post(`/admin/sender-profiles/${id}/test`, { to_email });

// ─── Subscribers — CSV import ─────────────────────────────────────────
export const importSubscribers = (rows) => api.post('/admin/subscribers/import', { rows });

// ─── Prospects ────────────────────────────────────────────────────────
export const listProspects = (params) => api.get('/admin/prospects', {
  params,
  paramsSerializer: (p) => {
    const sp = new URLSearchParams();
    Object.entries(p || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        v.forEach((item) => { if (item !== undefined && item !== null && item !== '') sp.append(k, item); });
      } else {
        sp.append(k, v);
      }
    });
    return sp.toString();
  },
});
export const getProspectFilters = () => api.get('/admin/prospects/filters');
export const getProspectStats = () => api.get('/admin/prospects/stats');
export const createProspect = (data) => api.post('/admin/prospects', data);
export const updateProspect = (id, data) => api.patch(`/admin/prospects/${id}`, data);
export const deleteProspect = (id) => api.delete(`/admin/prospects/${id}`);
export const importProspects = (rows) => api.post('/admin/prospects/import', { rows });
export const qualificationImport = (rows, dryRun = true) =>
  api.post('/admin/prospects/qualification-import', { rows, mode: 'update_existing', dry_run: dryRun });
export const convertProspectToSubscriber = (id, body) =>
  api.post(`/admin/prospects/${id}/convert-to-subscriber`, body);
export const bulkProspectAction = (body) => api.post('/admin/prospects/bulk', body);
export const autoClassifyProspectTiers = ({ dryRun = true } = {}) =>
  api.post(`/admin/prospects/auto-classify-tiers?dry_run=${dryRun ? 'true' : 'false'}`);
export const autoAssignProspectMarkets = ({ dryRun = true, overwrite = false } = {}) =>
  api.post(`/admin/prospects/auto-assign-markets?dry_run=${dryRun ? 'true' : 'false'}&overwrite=${overwrite ? 'true' : 'false'}`);

// ─── Reusable email templates (Send Email modal) ──────────────────────
export const listEmailTemplates = (templateType = 'prospect_outreach') =>
  api.get(`/admin/email-templates?template_type=${encodeURIComponent(templateType)}`);
export const createEmailTemplate = (body) => api.post('/admin/email-templates', body);
export const updateEmailTemplate = (id, body) => api.put(`/admin/email-templates/${id}`, body);
export const deleteEmailTemplate = (id) => api.delete(`/admin/email-templates/${id}`);
export const deleteAllProspects = (confirmation, acknowledged = false) =>
  api.post('/admin/prospects/delete-all', { confirmation, acknowledged });
export const enrichProspect = (id) => api.post(`/admin/prospects/${id}/enrich`);
export const bulkEnrichProspects = (body) => api.post('/admin/prospects/bulk-enrich', body);
export const getBulkEnrichStatus = (jobId) => api.get(`/admin/prospects/bulk-enrich/${jobId}`);
export const getProspectEmailTemplate = (id) => api.get(`/admin/prospects/email-template/${id}`);
export const sendEmailToProspect = (id, body) => api.post(`/admin/prospects/${id}/send-email`, body);
export const listProspectEmails = (id) => api.get(`/admin/prospects/${id}/emails`);
export const bulkSendEmail = (body) => api.post('/admin/prospects/bulk-send-email', body);
export const getBulkSendStatus = (jobId) => api.get(`/admin/prospects/bulk-send-email/${jobId}`);
export const cancelBulkSendJob = (jobId) => api.post(`/admin/prospects/bulk-send-email/${jobId}/cancel`);
export const getProspectEmailAnalytics = () => api.get('/admin/prospects/email-analytics');
export const resetProspectEmailAnalytics = (body = {}) => api.post('/admin/prospects/email-analytics/reset', body);
export const addProspectNote = (id, text) => api.post(`/admin/prospects/${id}/notes`, { text });
export const getProspectActivity = (id) => api.get(`/admin/prospects/${id}/activity`);
export const getProspectActivityTimeline = (id) => api.get(`/admin/prospects/${id}/activity-timeline`);
export const getCustomerActivityTimeline = (id) => api.get(`/admin/customers/${id}/activity-timeline`);

// Phase 6.1 — Hostinger IMAP read-only import
export const syncHostingerInbox = (days = 30) =>
  api.post(`/admin/email-import/sync-hostinger?days=${days}`);
export const getSourcingImportedEmails = (sourcingRequestId) =>
  api.get(`/admin/sourcing-requests/${sourcingRequestId}/imported-emails`);
export const exportProspects = (qs = '') =>
  api.get(`/admin/prospects/export${qs ? '?' + qs : ''}`, { responseType: 'blob' });
export const markEmailSent = (id) => api.post(`/admin/prospects/${id}/mark-email-sent`);
export const markWaSent = (id) => api.post(`/admin/prospects/${id}/mark-whatsapp-sent`);
export const markQuoteSent = (id) => api.post(`/admin/prospects/${id}/mark-quote-sent`);
export const markCustomer = (id) => api.post(`/admin/prospects/${id}/mark-customer`);
// ─── Hot Leads Inbox (Phase D1.5) ─────────────────────────────────────
export const listHotLeads = (params = {}) => api.get('/admin/hot-leads', { params });
export const markLeadContacted = (email, notes = '') =>
  api.post('/admin/hot-leads/mark-contacted', { email, notes });
export const unmarkLeadContacted = (email) =>
  api.delete(`/admin/hot-leads/mark-contacted/${encodeURIComponent(email)}`);
export const addLeadToCrm = (email) =>
  api.post('/admin/hot-leads/add-to-crm', { email });
export const ensureLeadProspect = (email) =>
  api.post('/admin/hot-leads/ensure-prospect', { email });
export const getProspectById = (id) => api.get(`/admin/prospects/${id}`);
export const createQuoteFromHotLead = (email) =>
  api.post('/admin/hot-leads/create-quote', { email });
// Phase 1 — bulk follow-up
export const sendBulkFollowUp = (payload) =>
  api.post('/admin/hot-leads/bulk-followup', payload);
export const sendBulkFollowUpPreview = (payload) =>
  api.post('/admin/hot-leads/bulk-followup/preview', payload);
export const bulkMarkContacted = (emails) =>
  api.post('/admin/hot-leads/bulk-mark-contacted', { emails });
// Phase 6.3 — Hot Leads matching-emails endpoint (Select All Matching)
export const getHotLeadsMatchingEmails = (params = {}) =>
  api.post('/admin/hot-leads/matching-emails', null, { params });

// Phase 6.5 — permanent default follow-up templates (Aug-2026: per-language).
export const getHotLeadsDefaultTemplates = () =>
  api.get('/admin/hot-leads/default-templates');
export const saveHotLeadsDefaultTemplate = (kind, subject, body, language = 'en') =>
  api.put('/admin/hot-leads/default-templates', { kind, subject, body, language });
export const resetHotLeadsDefaultTemplate = (kind, language = 'en') =>
  api.delete(`/admin/hot-leads/default-templates/${kind}?language=${encodeURIComponent(language)}`);






